import { useIsEmbeddedMobileLayout } from 'features/embeddedSidebar/hooks/useIsEmbeddedMobileLayout'
import type { DocumentPreviewPanelProps } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentPreview/DocumentPreviewPanel'
import type { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import { useActiveDocumentPreview } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useActiveDocumentPreview'
import {
  clearExplicitPreviewOpen,
  clearPreviewDismissals,
  closeDocumentPreview,
  INITIAL_DOCUMENT_PREVIEW_GATE,
  isDocumentPreviewVisible,
  openDocumentPreview,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocumentPreviewPanelState/documentPreviewGate'
import { transformSystemDocumentToTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/helpers'
import { useSystemDocumentByBqId } from 'features/knowledgeDocumentViewer/hooks/useSystemDocumentsByBqIds'
import type { ViewMode } from 'features/knowledgeDocumentViewer/types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { DocumentsViewerContentFilter } from 'routes/documents/-searchParams'

export interface UseDocumentPreviewPanelStateParams {
  activeIndex: number
  contentFilter: DocumentsViewerContentFilter
  documentResults: DocumentTableRow[]
  filteredDocuments: DocumentTableRow[]
  isLinkedDocsMode: boolean
  onSearchDocumentNavigate: (
    doc: DocumentTableRow,
    newTab: boolean,
    isLinkedDocsMode: boolean,
    target?: { pageNumber?: number; pageNum?: number; fragmentText?: string }
  ) => void
  onSwitchPreviewDocument?: (doc: DocumentTableRow, pageNum: number) => void
  selectedDocumentId: string | null
  initialPage?: number
  activeDocumentOverride?: DocumentTableRow | null
  viewMode: ViewMode
}

export interface UseDocumentPreviewPanelStateResult {
  activeDocument: DocumentTableRow | null
  documentPreviewPanelProps: Omit<DocumentPreviewPanelProps, 'onAddToContext'>
  handleOpenPreview: () => void
  isLoadingPreview: boolean
  isPreviewOpen: boolean
}

export function useDocumentPreviewPanelState({
  activeIndex,
  contentFilter,
  documentResults,
  filteredDocuments,
  isLinkedDocsMode,
  onSearchDocumentNavigate,
  onSwitchPreviewDocument,
  selectedDocumentId,
  initialPage = 1,
  activeDocumentOverride = null,
  viewMode,
}: UseDocumentPreviewPanelStateParams): UseDocumentPreviewPanelStateResult {
  const {
    document: selectedDocumentEntry,
    isLoading: isLoadingSelectedDocument,
  } = useSystemDocumentByBqId(
    viewMode === 'table' ? (selectedDocumentId ?? undefined) : undefined
  )

  const selectedTableDocument = useMemo(() => {
    if (!selectedDocumentEntry) return null
    return transformSystemDocumentToTableRow(selectedDocumentEntry)
  }, [selectedDocumentEntry])

  const { activeDocument, isPreviewOpen, isLoadingPreview } =
    useActiveDocumentPreview({
      documentResults:
        viewMode === 'card' ? filteredDocuments : documentResults,
      activeIndex,
      selectedDocumentId,
      selectedTableDocument,
      isLoadingSelectedDocument,
      contentFilter,
      viewMode,
    })
  const previewDocument = activeDocumentOverride ?? activeDocument
  const requireExplicitMobileOpen = useIsEmbeddedMobileLayout()
  const [gate, setGate] = useState(INITIAL_DOCUMENT_PREVIEW_GATE)
  const activeDocumentId = previewDocument?.id

  useEffect(() => {
    setGate(clearPreviewDismissals)
  }, [activeDocumentId, contentFilter, viewMode])

  useEffect(() => {
    setGate(clearExplicitPreviewOpen)
  }, [contentFilter, viewMode])

  const handleOpenPreview = useCallback(() => {
    setGate(openDocumentPreview())
  }, [])

  const shouldShowPreview = isDocumentPreviewVisible({
    viewMode,
    isPreviewOpen,
    requireExplicitOpen: requireExplicitMobileOpen,
    gate,
  })

  const handleClosePreview = useCallback(() => {
    setGate((current) =>
      closeDocumentPreview(current, viewMode, requireExplicitMobileOpen)
    )
  }, [requireExplicitMobileOpen, viewMode])

  const handleSwitchPreviewDocument = useCallback(
    (doc: DocumentTableRow, pageNum: number) => {
      setGate(clearPreviewDismissals)
      onSwitchPreviewDocument?.(doc, pageNum)
    },
    [onSwitchPreviewDocument]
  )

  const documentPreviewPanelProps: Omit<
    DocumentPreviewPanelProps,
    'onAddToContext'
  > = {
    document: previewDocument,
    isLinkedDocsMode,
    isOpen: shouldShowPreview,
    isLoading: isLoadingPreview,
    onClose: handleClosePreview,
    onDocumentNavigate: onSearchDocumentNavigate,
    onSwitchPreviewDocument: handleSwitchPreviewDocument,
    initialPage,
  }

  return {
    activeDocument,
    documentPreviewPanelProps,
    handleOpenPreview,
    isLoadingPreview,
    isPreviewOpen: shouldShowPreview,
  }
}
